import { z } from 'zod';
import { eventNameSchema, propertyValueSchema } from './event';

// Segment rule AST (§6): AND/OR trees over trait + event predicates, stored
// as JSONB in segments.definition. Data-only — no expression language, so it
// can safely be the target of NL-generated segments later (§9). Unknown
// kind/op values fail parse, which is the version gate: no schema_version
// field needed, additive changes only.
//
// Every bound here (string caps, list caps, depth/node caps) is part of the
// cross-implementation contract: a future native/Rust evaluator only has to
// handle documents inside these bounds. Loosening a bound later is additive
// and safe; tightening one is a breaking change.

export const equalityOpSchema = z.enum(['eq', 'neq']);
export const orderingOpSchema = z.enum(['gt', 'gte', 'lt', 'lte']);
export const comparisonOpSchema = z.enum(['eq', 'neq', 'gt', 'gte', 'lt', 'lte']);
export type ComparisonOp = z.infer<typeof comparisonOpSchema>;

export const membershipOpSchema = z.enum(['in', 'not_in']);
export type MembershipOp = z.infer<typeof membershipOpSchema>;

// Same value domain as event properties (propertyValueSchema, string ≤1000):
// trait values and event properties are compared against the same rules.
const scalarSchema = propertyValueSchema;
export type Scalar = z.infer<typeof scalarSchema>;

const traitNameSchema = z.string().min(1).max(100);

// Trait predicate: evaluated live against the profile store (§6).
// Ordering ops require a numeric value — the evaluator only orders numbers,
// so a rule like `score gt "high"` would be stored-but-unsatisfiable if the
// schema accepted it.
export const traitPredicateSchema = z.union([
  z.object({
    kind: z.literal('trait'),
    trait: traitNameSchema,
    op: equalityOpSchema,
    value: scalarSchema,
  }),
  z.object({
    kind: z.literal('trait'),
    trait: traitNameSchema,
    op: orderingOpSchema,
    value: z.number(),
  }),
  z.object({
    kind: z.literal('trait'),
    trait: traitNameSchema,
    op: membershipOpSchema,
    value: z.array(scalarSchema).min(1).max(100),
  }),
]);
export type TraitPredicate = z.infer<typeof traitPredicateSchema>;

// Event predicate: count-in-window, e.g. "did deposit_completed >= 3 times
// in the last 7 days" (§6). op compares the observed count to `count`.
// `event` shares the event contract's snake_case name format so a segment
// can't reference an event name that ingestion would never accept.
export const eventPredicateSchema = z
  .object({
    kind: z.literal('event'),
    event: eventNameSchema,
    op: comparisonOpSchema,
    count: z.number().int().nonnegative().max(10_000),
    window_days: z.number().int().positive().max(90),
  })
  .refine((p) => !(p.op === 'lt' && p.count === 0), {
    message: '"count lt 0" can never be satisfied — observed counts are >= 0',
  });
export type EventPredicate = z.infer<typeof eventPredicateSchema>;

export interface RuleGroup {
  kind: 'and' | 'or';
  rules: RuleNode[];
}
export type RuleNode = TraitPredicate | EventPredicate | RuleGroup;

export const ruleNodeSchema: z.ZodType<RuleNode> = z.lazy(() =>
  z.union([
    traitPredicateSchema,
    eventPredicateSchema,
    z.object({
      kind: z.enum(['and', 'or']),
      rules: z.array(ruleNodeSchema).min(1).max(MAX_RULE_NODES),
    }),
  ])
);

export const MAX_RULE_DEPTH = 32;
export const MAX_RULE_NODES = 512;

// Iterative bounds walk over the RAW document, run BEFORE the recursive zod
// parse: zod's lazy-union parse recurses per node, so without this pre-check
// a hostile ~2000-deep document blows the call stack (RangeError escaping
// even safeParse) and an arbitrarily wide one burns full parse cost before
// the node budget could reject it. Runs on untrusted input: dashboard users
// today, LLM-generated segments later.
function checkRawBounds(raw: unknown, ctx: z.RefinementCtx): void {
  let nodes = 0;
  let maxDepth = 0;
  const stack: Array<{ value: unknown; depth: number }> = [{ value: raw, depth: 1 }];
  while (stack.length > 0) {
    const { value, depth } = stack.pop() as { value: unknown; depth: number };
    nodes++;
    if (depth > maxDepth) maxDepth = depth;
    if (nodes > MAX_RULE_NODES || maxDepth > MAX_RULE_DEPTH) break; // cheap early exit
    if (typeof value === 'object' && value !== null) {
      const rules = (value as { rules?: unknown }).rules;
      if (Array.isArray(rules)) {
        for (const child of rules) stack.push({ value: child, depth: depth + 1 });
      }
    }
  }
  if (maxDepth > MAX_RULE_DEPTH) {
    ctx.addIssue({ code: 'custom', message: `rule tree deeper than ${MAX_RULE_DEPTH} levels` });
  }
  if (nodes > MAX_RULE_NODES) {
    ctx.addIssue({ code: 'custom', message: `rule tree has more than ${MAX_RULE_NODES} nodes` });
  }
}

// Root schema for segments.definition JSONB: bounds first, then the tree.
export const segmentRuleSchema = z.unknown().superRefine(checkRawBounds).pipe(ruleNodeSchema);
